import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { UseStepFunc, defaultStepFormProps } from '../types.d'
import { Route, Switch } from 'react-router-dom'
import * as routing from '../../constants/routes'
import { OnboardingStepKey, OnboardingStepStatus } from '@cbgms/api'
import { SetUpOrderAccountsScreen } from './SetUpOrderAccountsScreen'
import AdditionalWholesalers from './AdditionalWholesalers'
import { AdditionalWholesaler } from './types.d'
import { setNewDefaultOrderAccount } from 'app/settings/state/DefaultOrderAccount/actions'
import { useDispatch } from 'react-redux'
import { useCompleteOnSubmit } from '../use-complete-on-submit'
import { useTranslation } from 'react-i18next'
import { onboardingLinkLicenses } from 'app/auth/state/actions'
import { useShouldExecuteStep } from '../use-should-execute-step'
import { useDefaultSkipSetting } from '../use-default-skip-setting'
import { Subscriptions } from '@carsys/enums/subscription'
import { SetupWholesalerCTA } from './SetupWholesalerCTA'
import { useStepData } from '../use-step-data'
import useGetUnfilteredOnboardingWholesalers from 'app/auth/components/hooks/use-get-unfiltered-onboarding-wholesaler'
import { ValidatedWholesalerCredentials } from '../OnboardingWholesalerCredentialsStep/types.d'
import GenericDuplicationError from 'app/auth/components/onboarding/steps/shared/GenericDuplicationError'
import { useStoreOnboardingWholesalerCredentials } from '../use-store-onboarding-wholesaler-credentials'

interface SetupWholesalerStepProps {
  basePath: string
  addAdditionalWholesaler: (a: AdditionalWholesaler) => void
  deleteAdditionalWholesaler: (a: AdditionalWholesaler) => void
  onSubmit: (args?: any) => void
  fixedWholesaler: AdditionalWholesaler | null
  additionalWholesalers: AdditionalWholesaler[]
  modules: Subscriptions[]
  country: string
}
interface StepInputProps {
  basePath: string
  wholesalerCredentials: AdditionalWholesaler | null
  ticketUUID: string | null
}

interface StepOutput {
  additionalWholesalers: AdditionalWholesaler[]
}

export const useSetupWholesalerStep: UseStepFunc<SetupWholesalerStepProps, StepInputProps, StepOutput> = ({
  basePath,
  wholesalerCredentials,
  onboardingInfo,
  ticketUUID
}) => {
  const dispatch = useDispatch()
  const stepName = OnboardingStepKey.OrderAccounts
  const stepData = useStepData(stepName, onboardingInfo)
  const storeOnboardingWholesalerCredentials = useStoreOnboardingWholesalerCredentials()
  const shouldExecuteStep = useShouldExecuteStep(stepName, onboardingInfo) || !!wholesalerCredentials
  // Also run this step in the background even when its off. Creates the original order account
  const shouldRunInBackground = stepData.State === OnboardingStepStatus.Background || stepData.State === OnboardingStepStatus.Off
  // Handle initial data, these should all be added to the account in case they where provided.
  const { isInitialLoading, data: wholesalers } = useGetUnfilteredOnboardingWholesalers()
  // map the wholesaler information so that the preconfigured items show up correctly on the ui
  const preconfiguredOrderAccounts = useMemo(() => {
    return (stepData.InitialData?.AdditionalOrderAccounts || []).map((ac: ValidatedWholesalerCredentials) => ({
      wholesaler: wholesalers?.find(w => w.UUID === ac.WholesalerUUID),
      ...ac
    }))
  }, [stepData.InitialData, isInitialLoading, wholesalers])
  // Keep state of all accounts to add.
  const [additionalWholesalers, setAdditionalWholesalers] = useState<AdditionalWholesaler[]>(preconfiguredOrderAccounts)
  // side effect to set the preconfigured accounts just in case the the wholesalers where not loaded yet
  useEffect(() => {
    preconfiguredOrderAccounts?.length && setAdditionalWholesalers(preconfiguredOrderAccounts)
  }, [preconfiguredOrderAccounts])

  const hideSkip = useDefaultSkipSetting(onboardingInfo)
  const complete = useCompleteOnSubmit(() => {})
  const addAdditionalWholesaler = useCallback(
    (a: AdditionalWholesaler) => setAdditionalWholesalers([...additionalWholesalers, a]),
    [setAdditionalWholesalers, additionalWholesalers]
  )
  const deleteAdditionalWholesaler = useCallback(
    (a: AdditionalWholesaler) =>
      setAdditionalWholesalers(
        // remove the account from the list
        additionalWholesalers.filter(f => !(f.WholesalerUUID === a.WholesalerUUID && f.Credentials.UserId === a.Credentials.UserId))
      ),
    [setAdditionalWholesalers, additionalWholesalers]
  )

  const onPersist = useCallback(async () => {
    // create an order account for the passed credentials
    const shouldSetFirstAsDefault = !wholesalerCredentials
    if (wholesalerCredentials) {
      const response = await storeOnboardingWholesalerCredentials(
        wholesalerCredentials.Credentials,
        wholesalerCredentials?.WholesalerUUID,
        ticketUUID
      )
      await dispatch(setNewDefaultOrderAccount(response.Data.UUID))
    }

    for (let i = 0; i < additionalWholesalers.length; i++) {
      const response = await storeOnboardingWholesalerCredentials(
        additionalWholesalers[i].Credentials,
        additionalWholesalers[i]?.WholesalerUUID,
        ticketUUID
      )

      if (shouldSetFirstAsDefault && i === 0) {
        await dispatch(setNewDefaultOrderAccount(response.Data.UUID))
      }
    }
    await dispatch(onboardingLinkLicenses())
  }, [dispatch, wholesalerCredentials, additionalWholesalers])

  return {
    Component: SetupWholesalerStep,
    CTAComponent: SetupWholesalerCTA,
    StepperText,
    isCompleted: complete.isCompleted,
    showInStepper: true,
    showStepperHeader: true,
    step: stepName,
    stepData,
    shouldExecuteStep,
    shouldRunInBackground,
    hideSkip,
    stepProps: {
      basePath,
      addAdditionalWholesaler,
      deleteAdditionalWholesaler,
      onSubmit: complete.onSubmit,
      fixedWholesaler: wholesalerCredentials,
      additionalWholesalers,
      modules: onboardingInfo.modules,
      country: onboardingInfo.country,
      duplicationCheck: stepData.DuplicationCheck || [],
      onboardingInfo
    },
    persister: {
      onPersist,
      persistOrder: 20
    },
    additionalWholesalers
  }
}

const StepperText: React.FC<{}> = () => {
  const { t } = useTranslation()
  return <>{t('onboarding.step_title_add_order_account')}</>
}

export const SetupWholesalerStep: React.FC<defaultStepFormProps & SetupWholesalerStepProps> = ({ basePath, ...rest }) => {
  return (
    <Switch>
      <Route path={routing.pathDuplicationError(basePath)} component={GenericDuplicationError} />
      <Route
        path={routing.pathAddAdditionalOrderAccounts(basePath)}
        render={() => <AdditionalWholesalers basePath={basePath} {...rest} />}
      />
      <Route path={routing.pathSetUpOrderAccounts(basePath)} render={() => <SetUpOrderAccountsScreen basePath={basePath} {...rest} />} />
    </Switch>
  )
}
